import React from 'react';
import PropTypes from 'prop-types';
import { combineReducers } from 'redux';
import { Provider } from 'react-redux';
import { ServerLocation } from '@reach/router';
import mergeDeep from 'deepmerge';

import { configureStore } from 'store';
import { getRequestUrl } from 'store/ProductDetails/selectors';
import { commonPageLoaded } from 'store/common/settings/actions';

import AdaptivityProvider from '../containers/AdaptivityProvider';

/**
 * Takes a compiled `baseRootReducer` function and injects `widgets` key on top level
 * with all the reducers from `widgetReducerDict`
 *
 * @param baseRootReducer {function} project root reducer
 * @param widgetReducersDict {Object} a dict containing widget reducers
 */
export const injectWidgetsReducer = (baseRootReducer, widgetReducersDict) => {
  if (Object.keys(widgetReducersDict).length) {
    const combinedWidgetReducer = combineReducers(widgetReducersDict);

    /** state here represents self managed store piece */
    const widgetsRootReducer = (state, action) => ({
      ...state,
      ...combinedWidgetReducer(state, action),
    });

    /** inject widgets subState into baseRootReducer */
    return (state = {}, action = {}) => ({
      ...baseRootReducer(state, action),
      widgets: widgetsRootReducer(state.widgets, action),
    });
  }

  return baseRootReducer;
};

/** client-side Placeholder for LocationProvider */
const LocationProviderDummy = ({ children }) => children;
LocationProviderDummy.propTypes = {
  children: PropTypes.node,
};

/** Provide url for SSR, or simply ignored for client */
const LocationProvider = typeof window === 'undefined' ? ServerLocation : LocationProviderDummy;

/**
 * HOC for connecting App to everything - store, routing, adaptivity, etc...
 * @param rootReducer {function} - used as a root for redux store
 * @param mapPropsToState {function} - takes data given by the model, returns modified data used by the App
 * @param widgets { Object } - an array of arrays for widgets, have same format as first two arguments
 *   @property {any} - each key is redux store key and each value is an array of [
 *     widgetReducer {function} - reducer for widget data
 *     mapWidgetPropsToState {function} - takes widget data and entire model, returns new widget data
 *   ]
 */
const connectProvider =
  (rootReducer, mapPropsToState = (d) => d, widgets = {}) =>
  (WrappedComponent) => {
    class ProviderConnector extends React.Component {
      constructor(props) {
        super(props);

        const initialData = {
          ...mapPropsToState(props),
          widgets: {},
        };

        const widgetReducersDict = {};

        /**
         * if widgets are not empty, we must apply their data into store
         */
        Object.keys(widgets).forEach((widgetAlias) => {
          const [widgetReducer, mapWidgetPropsToState] = widgets[widgetAlias];

          /** create a model.widgets.%myWidget% key within the initialData */
          if (mapWidgetPropsToState) {
            initialData.widgets[widgetAlias] = mapWidgetPropsToState(
              props.widgets[widgetAlias],
              props,
            );
          }

          /** create a large widgetReducersDict by combining widget root reducers */
          if (widgetReducer) widgetReducersDict[widgetAlias] = widgetReducer;
        });

        /** inject requestUrl for server rendering */
        const requestUrl = getRequestUrl(initialData)
          .replace(/^https?:\/\/[^/]+/, '')
          .replace(/\?.*/, '');

        const modifiedRootReducer = injectWidgetsReducer(rootReducer, widgetReducersDict);

        /**
         * run reducer once to extract initial value
         * then deep merge real data on top to preserve both
         */
        const initialState = modifiedRootReducer(undefined, {});
        const mergedState = mergeDeep(initialState, initialData, {
          arrayMerge: (_, sourceArray) => sourceArray,
        });

        this.store = configureStore(mergedState, modifiedRootReducer);
        this.requestUrl = requestUrl;
      }

      componentDidMount() {
        this.store.dispatch(commonPageLoaded());
      }

      render() {
        return (
          <Provider store={this.store}>
            <LocationProvider url={this.requestUrl}>
              <AdaptivityProvider>
                <WrappedComponent />
              </AdaptivityProvider>
            </LocationProvider>
          </Provider>
        );
      }
    }

    ProviderConnector.propTypes = {
      /** a key/value storage containing all widget data */
      widgets: PropTypes.oneOfType([PropTypes.object]),
    };

    ProviderConnector.defaultProps = {
      widgets: {},
    };

    return ProviderConnector;
  };

export default connectProvider;
